import { useState, useEffect } from 'react';
import api from '../utils/api';
import { toast } from 'react-hot-toast';

const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const DAY_NAMES = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

function formatCurrency(n) {
  if (!n) return '$0';
  return '$' + parseFloat(n).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

function getMonthKey(year, month) {
  return year + '-' + String(month + 1).padStart(2, '0');
}

function getDayKey(year, month, d) {
  return year + '-' + String(month + 1).padStart(2, '0') + '-' + String(d).padStart(2, '0');
}

export default function CalendarPage() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth());
  const [entries, setEntries] = useState({});
  const [autoEntries, setAutoEntries] = useState([]);
  const [selectedDay, setSelectedDay] = useState(null);
  const [newEntry, setNewEntry] = useState({ person: '', amount: '' });
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const monthKey = getMonthKey(year, month);

  useEffect(() => { loadMonth(); loadAutoEntries(); setSelectedDay(null); }, [year, month]);

  async function loadMonth() {
    try {
      const res = await api.get('/calendar/' + monthKey);
      const data = res.data;
      const ent = data.entries;
      setEntries(ent && typeof ent === 'object' && !Array.isArray(ent) ? ent : {});
      setDirty(false);
    } catch { setEntries({}); }
  }

  async function loadAutoEntries() {
    try {
      const res = await api.get('/calendar/autopop/' + monthKey);
      setAutoEntries(Array.isArray(res.data) ? res.data : []);
    } catch { setAutoEntries([]); }
  }

  async function handleSave() {
    setSaving(true);
    try {
      await api.post('/calendar', { week_start: monthKey, entries });
      toast.success('Saved!');
      setDirty(false);
    } catch {
      toast.error('Failed to save');
    } finally { setSaving(false); }
  }

  function addEntry() {
    if (!newEntry.person.trim() || !newEntry.amount) return toast.error('Enter person and amount');
    if (!selectedDay) return toast.error('Select a day first');
    const entry = { id: Date.now(), person: newEntry.person.trim(), amount: parseFloat(newEntry.amount), collected: false };
    setEntries(prev => ({ ...prev, [selectedDay]: [...(prev[selectedDay] || []), entry] }));
    setNewEntry({ person: '', amount: '' });
    setDirty(true);
  }

  function toggleCollected(day, id) {
    setEntries(prev => ({ ...prev, [day]: (prev[day] || []).map(e => e.id === id ? { ...e, collected: !e.collected } : e) }));
    setDirty(true);
  }

  function removeEntry(day, id) {
    setEntries(prev => ({ ...prev, [day]: (prev[day] || []).filter(e => e.id !== id) }));
    setDirty(true);
  }

  // Calendar grid
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const calCells = [];
  for (let i = 0; i < firstDay; i++) calCells.push(null);
  for (let d = 1; d <= daysInMonth; d++) calCells.push(d);
  while (calCells.length % 7 !== 0) calCells.push(null);

  function getAutoForDay(dayKey) {
    return autoEntries.filter(e => e.payment_date === dayKey);
  }

  // Monthly totals
  const allManual = Object.values(entries).flat();
  const manualProjected = allManual.reduce((s, e) => s + (e.amount || 0), 0);
  const manualCollected = allManual.filter(e => e.collected).reduce((s, e) => s + (e.amount || 0), 0);
  const autoDue = autoEntries.filter(e => e.status === 'Due').reduce((s, e) => s + (parseFloat(e.amount) || 0), 0);
  const autoCollected = autoEntries.filter(e => e.status === 'Collected').reduce((s, e) => s + (parseFloat(e.amount) || 0), 0);
  const totalProjected = manualProjected + autoDue + autoCollected;
  const totalCollected = manualCollected + autoCollected;
  const totalPending = totalProjected - totalCollected;

  const today = new Date();
  const isToday = (d) => d === today.getDate() && month === today.getMonth() && year === today.getFullYear();

  function prevMonth() {
    if (month === 0) { setYear(y => y - 1); setMonth(11); } else setMonth(m => m - 1);
  }
  function nextMonth() {
    if (month === 11) { setYear(y => y + 1); setMonth(0); } else setMonth(m => m + 1);
  }

  const selectedDayAuto = selectedDay ? getAutoForDay(selectedDay) : [];
  const selectedDayManual = selectedDay ? (entries[selectedDay] || []) : [];

  return (
    <>
      <div className="page-header">
        <h2>Collection Calendar</h2>
        <div className="page-header-actions">
          <button className="btn btn-outline btn-sm" onClick={prevMonth}>← Prev</button>
          <span style={{ fontWeight: 700, fontSize: 15, minWidth: 160, textAlign: 'center' }}>{MONTH_NAMES[month]} {year}</span>
          <button className="btn btn-outline btn-sm" onClick={nextMonth}>Next →</button>
          <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>

      <div className="page-body">
        {dirty && <div style={{ marginBottom: 12, color: '#F57F17', fontWeight: 600, fontSize: 13 }}>● Unsaved changes</div>}

        {/* Summary cards */}
        <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
          <SummaryCard label="Total Projected" value={formatCurrency(totalProjected)} color="#0D3B2E" sub="Due payments + manual entries" />
          <SummaryCard label="Collected" value={formatCurrency(totalCollected)} color="#1B5E20" sub="From records + checked manual" />
          <SummaryCard label="Pending" value={formatCurrency(totalPending)} color={totalPending > 0 ? '#F57F17' : '#1B5E20'} sub="Still to be collected" />
          {totalProjected > 0 && <SummaryCard label="% Collected" value={Math.round((totalCollected/totalProjected)*100)+'%'} color="#1565C0" sub="Of total projected" />}
          <div style={{ background: '#fff', border: '1px solid #eee', borderRadius: 8, padding: '10px 16px', fontSize: 12 }}>
            <div style={{ fontSize: 11, color: '#777', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>From Records</div>
            <div style={{ color: '#E65100', marginBottom: 2 }}>● Due: {formatCurrency(autoDue)}</div>
            <div style={{ color: '#1B5E20' }}>● Collected: {formatCurrency(autoCollected)}</div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start', flexWrap: 'wrap', flexDirection: 'column' }}>
          {/* Calendar */}
          <div style={{ flex: 2, minWidth: 0, width: '100%' }}>
            <div style={{ background: '#fff', borderRadius: 10, boxShadow: '0 1px 4px rgba(0,0,0,0.08)', overflow: 'hidden', overflowX: 'auto' }}><div style={{ minWidth: 560 }}>
              {/* Day headers */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', background: '#0D3B2E' }}>
                {DAY_NAMES.map(d => (
                  <div key={d} style={{ padding: '8px 4px', textAlign: 'center', color: '#fff', fontSize: 12, fontWeight: 700 }}>{d}</div>
                ))}
              </div>
              {/* Day cells */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
                {calCells.map((d, idx) => {
                  if (!d) return <div key={idx} style={{ minHeight: 85, background: '#fafafa', borderRight: '1px solid #eee', borderBottom: '1px solid #eee' }} />;
                  const dayKey = getDayKey(year, month, d);
                  const dayAuto = getAutoForDay(dayKey);
                  const dayManual = entries[dayKey] || [];
                  const isSelected = selectedDay === dayKey;
                  const hasEntries = dayAuto.length > 0 || dayManual.length > 0;

                  const dayProjected = dayAuto.reduce((s,e)=>s+(parseFloat(e.amount)||0),0) + dayManual.reduce((s,e)=>s+(e.amount||0),0);
                  const dayCollected = dayAuto.filter(e=>e.status==='Collected').reduce((s,e)=>s+(parseFloat(e.amount)||0),0) + dayManual.filter(e=>e.collected).reduce((s,e)=>s+(e.amount||0),0);

                  return (
                    <div key={idx}
                      onClick={() => setSelectedDay(isSelected ? null : dayKey)}
                      style={{
                        minHeight: 85, padding: '4px 5px',
                        borderRight: '1px solid #eee', borderBottom: '1px solid #eee',
                        cursor: 'pointer',
                        background: isSelected ? '#e8f5e9' : '#fff',
                        outline: isSelected ? '2px solid #1B5E20' : 'none',
                        outlineOffset: -2,
                      }}>
                      {/* Date number */}
                      <div style={{
                        fontWeight: 700, fontSize: 12, marginBottom: 3,
                        width: 22, height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center',
                        borderRadius: '50%',
                        background: isToday(d) ? '#0D3B2E' : 'transparent',
                        color: isToday(d) ? '#fff' : '#333',
                      }}>{d}</div>

                      {/* Auto entries */}
                      {dayAuto.map(e => (
                        <div key={'a'+e.id} title={e.customer_name + ' — ' + e.payment_type + ' — ' + e.po_number} style={{
                          fontSize: 9, padding: '1px 4px', borderRadius: 3, marginBottom: 1,
                          background: e.status === 'Collected' ? '#c8e6c9' : '#ffe0b2',
                          color: e.status === 'Collected' ? '#1B5E20' : '#bf360c',
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                          textDecoration: e.status === 'Collected' ? 'line-through' : 'none',
                          borderLeft: '2px solid ' + (e.status === 'Collected' ? '#1B5E20' : '#F57F17'),
                        }}>
                          {e.customer_name} {formatCurrency(e.amount)}
                        </div>
                      ))}

                      {/* Manual entries */}
                      {dayManual.map(e => (
                        <div key={'m'+e.id} style={{
                          fontSize: 9, padding: '1px 4px', borderRadius: 3, marginBottom: 1,
                          background: e.collected ? '#c8e6c9' : '#e3f2fd',
                          color: e.collected ? '#1B5E20' : '#1565C0',
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                          textDecoration: e.collected ? 'line-through' : 'none',
                          borderLeft: '2px solid ' + (e.collected ? '#1B5E20' : '#1565C0'),
                        }}>
                          ★ {e.person} {formatCurrency(e.amount)}
                        </div>
                      ))}

                      {/* Day totals */}
                      {hasEntries && (
                        <div style={{ fontSize: 9, color: '#888', marginTop: 2 }}>
                          {formatCurrency(dayCollected)}/{formatCurrency(dayProjected)}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div></div>

            {/* Legend */}
            <div style={{ display: 'flex', gap: 16, marginTop: 10, fontSize: 11, color: '#555', flexWrap: 'wrap' }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <span style={{ display: 'inline-block', width: 12, height: 12, background: '#ffe0b2', borderLeft: '3px solid #F57F17', borderRadius: 2 }}></span> Due (from records)
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <span style={{ display: 'inline-block', width: 12, height: 12, background: '#c8e6c9', borderLeft: '3px solid #1B5E20', borderRadius: 2 }}></span> Collected (from records)
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <span style={{ display: 'inline-block', width: 12, height: 12, background: '#e3f2fd', borderLeft: '3px solid #1565C0', borderRadius: 2 }}></span> ★ Manual entry
              </span>
            </div>
          </div>

          {/* Side panel */}
          <div style={{ flex: 1, minWidth: 260, position: 'sticky', top: 80 }}>
            {/* Add entry */}
            <div style={{ background: '#fff', borderRadius: 10, padding: 16, boxShadow: '0 1px 4px rgba(0,0,0,0.08)', marginBottom: 14 }}>
              <div style={{ fontWeight: 700, color: '#0D3B2E', marginBottom: 12, fontSize: 14 }}>
                {selectedDay ? '+ Add to ' + selectedDay : 'Click a day to add entry'}
              </div>
              <div style={{ marginBottom: 10 }}>
                <label style={lbl}>Person</label>
                <input className="form-input" placeholder="Name" value={newEntry.person}
                  onChange={e => setNewEntry(n => ({ ...n, person: e.target.value }))}
                  onKeyDown={e => e.key === 'Enter' && addEntry()} disabled={!selectedDay} />
              </div>
              <div style={{ marginBottom: 12 }}>
                <label style={lbl}>Amount ($)</label>
                <input className="form-input" type="number" placeholder="0" value={newEntry.amount}
                  onChange={e => setNewEntry(n => ({ ...n, amount: e.target.value }))}
                  onKeyDown={e => e.key === 'Enter' && addEntry()} disabled={!selectedDay} />
              </div>
              <button className="btn btn-primary" style={{ width: '100%' }} onClick={addEntry} disabled={!selectedDay}>
                + Add Manual Entry
              </button>
            </div>

            {/* Selected day detail */}
            {selectedDay && (selectedDayAuto.length > 0 || selectedDayManual.length > 0) && (
              <div style={{ background: '#fff', borderRadius: 10, padding: 16, boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
                <div style={{ fontWeight: 700, color: '#0D3B2E', marginBottom: 12, fontSize: 14 }}>{selectedDay}</div>

                {selectedDayAuto.length > 0 && (
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#aaa', textTransform: 'uppercase', marginBottom: 8 }}>From Records</div>
                    {selectedDayAuto.map(e => (
                      <div key={e.id} style={{ display: 'flex', gap: 10, padding: '8px 0', borderBottom: '1px solid #f5f5f5' }}>
                        <div style={{ width: 8, borderRadius: 4, background: e.status === 'Collected' ? '#1B5E20' : '#F57F17', flexShrink: 0, alignSelf: 'stretch' }} />
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 600, fontSize: 13 }}>{e.customer_name}</div>
                          <div style={{ fontSize: 11, color: '#888' }}>{e.payment_type} · {e.service} · {e.po_number}</div>
                          <div style={{ fontSize: 13, fontWeight: 600, color: e.status === 'Collected' ? '#1B5E20' : '#E65100', marginTop: 2 }}>
                            {formatCurrency(e.amount)} <span style={{ fontWeight: 400, fontSize: 11 }}>· {e.status}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {selectedDayManual.length > 0 && (
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#aaa', textTransform: 'uppercase', marginBottom: 8 }}>Manual Entries</div>
                    {selectedDayManual.map(e => (
                      <div key={e.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0', borderBottom: '1px solid #f5f5f5' }}>
                        <input type="checkbox" checked={e.collected} onChange={() => toggleCollected(selectedDay, e.id)}
                          style={{ cursor: 'pointer', accentColor: '#1B5E20', width: 16, height: 16 }} />
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 600, fontSize: 13, textDecoration: e.collected ? 'line-through' : 'none', color: e.collected ? '#aaa' : '#333' }}>{e.person}</div>
                          <div style={{ fontSize: 13, color: '#1565C0', fontWeight: 600 }}>{formatCurrency(e.amount)}</div>
                        </div>
                        <button onClick={() => removeEntry(selectedDay, e.id)}
                          style={{ background: 'none', border: 'none', color: '#ccc', cursor: 'pointer', fontSize: 18, lineHeight: 1 }}>✕</button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Day totals */}
                <div style={{ marginTop: 12, paddingTop: 10, borderTop: '1px solid #eee', display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                  <div style={{ color: '#555' }}>Projected<br /><strong style={{ color: '#0D3B2E', fontSize: 16 }}>{formatCurrency(
                    selectedDayAuto.reduce((s,e)=>s+(parseFloat(e.amount)||0),0) +
                    selectedDayManual.reduce((s,e)=>s+(e.amount||0),0)
                  )}</strong></div>
                  <div style={{ color: '#555', textAlign: 'right' }}>Collected<br /><strong style={{ color: '#1B5E20', fontSize: 16 }}>{formatCurrency(
                    selectedDayAuto.filter(e=>e.status==='Collected').reduce((s,e)=>s+(parseFloat(e.amount)||0),0) +
                    selectedDayManual.filter(e=>e.collected).reduce((s,e)=>s+(e.amount||0),0)
                  )}</strong></div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

function SummaryCard({ label, value, color, sub }) {
  return (
    <div style={{ background: '#fff', border: '2px solid ' + color, borderRadius: 8, padding: '10px 18px', minWidth: 140 }}>
      <div style={{ fontSize: 11, color: '#777', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 700, color }}>{value}</div>
      {sub && <div style={{ fontSize: 10, color: '#aaa', marginTop: 3 }}>{sub}</div>}
    </div>
  );
}

const lbl = { display: 'block', fontSize: 11, fontWeight: 600, color: '#555', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 };
